import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useCallback } from 'react';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { TicketDocument } from '~/types/Ticket';

import { SortableDocumentItem } from './SortableDocumentItem';

interface SortableDocumentListProps {
  documents: Record<string, TicketDocument>;
  orderedIds: string[];
  validationErrors: Record<string, string | undefined>;
  onReorder: (ids: string[]) => void;
  onRemove?: (ticketId: string) => void;
  canRemove?: boolean;
}

export const SortableDocumentList = ({
  documents,
  orderedIds,
  validationErrors,
  onReorder,
  onRemove,
  canRemove,
}: SortableDocumentListProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = orderedIds.indexOf(String(active.id));
        const newIndex = orderedIds.indexOf(String(over.id));
        onReorder(arrayMove(orderedIds, oldIndex, newIndex));
      }
    },
    [orderedIds, onReorder],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={orderedIds}
        strategy={verticalListSortingStrategy}
      >
        <Layout direction="column" spacing={3}>
          {orderedIds.map((ticketId) => {
            const document = documents[ticketId];

            if (!document) return null;

            return (
              <SortableDocumentItem
                key={ticketId}
                id={ticketId}
                document={document}
                validationError={validationErrors[ticketId]}
                onRemove={onRemove}
                canRemove={canRemove}
              />
            );
          })}
        </Layout>
      </SortableContext>
    </DndContext>
  );
};
